/**
 * Send a message from the dashboard (D-53): a broadcast to every customer of an app (D-52), chosen customers,
 * or direct addresses, with content per channel and a live preview of what recipients see.
 * The JSON tab takes the full POST /v1/messages body for anything the form does not cover.
 */
import type { App, MessagePriority, SendMessageInput } from "@aiyo/shared";
import { Envelope, PaperPlane } from "@gravity-ui/icons";
import { Alert, Button, Tabs } from "@heroui/react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CustomerPicker } from "../components/CustomerPicker";
import { FormDialog } from "../components/dialogs";
import { CheckboxInput, SelectInput, SwitchInput, TextAreaInput, TextInput } from "../components/fields";
import { Segmented } from "../components/filters";
import { ChannelPreview, smsParts } from "../components/previews";
import {
  Callout,
  ChannelChip,
  EmptyBlock,
  ErrorBlock,
  KeyValue,
  LoadingBlock,
  Page,
  PageHeader,
  Panel,
} from "../components/ui";
import { validationIssues } from "../lib/api";
import { useFormat } from "../lib/format";
import { useApps, useAudience, useMe, useMessage, useProfile, useSendMessage } from "../lib/queries";
import {
  type AudienceKind,
  buildRequest,
  COMPOSE_CHANNELS,
  type ComposeChannel,
  type Draft,
  emptyDraft,
  enabledChannels,
  fromMessage,
  splitList,
  validate,
} from "./send/draft";

type Mode = "compose" | "json";

export function SendPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const apps = useApps();
  const { data: me } = useMe();
  const activeApps = (apps.data ?? []).filter((a) => a.status === "active");
  const app = activeApps.find((a) => a.id === params.get("app")) ?? activeApps[0];

  if (apps.isPending) return <LoadingBlock />;
  if (apps.isError) return <ErrorBlock error={apps.error} onRetry={() => apps.refetch()} />;
  return (
    <Page>
      <PageHeader title={t("send.title")} description={t("send.subtitle")} />
      {me?.role === "viewer" ? (
        <Callout tone="info">{t("send.viewer")}</Callout>
      ) : !app ? (
        <Panel>
          <EmptyBlock
            icon={PaperPlane}
            title={t("send.noApps")}
            hint={t("send.noAppsHint")}
            action={
              <Link to="/apps" className="text-sm text-accent hover:underline">
                {t("nav.apps")}
              </Link>
            }
          />
        </Panel>
      ) : (
        <Composer
          key={app.id}
          app={app}
          apps={activeApps}
          onAppChange={(id) => setParams({ app: id }, { replace: true })}
          customer={params.get("customer")}
          fromMessageId={params.get("from")}
        />
      )}
    </Page>
  );
}

function Composer({
  app,
  apps,
  onAppChange,
  customer,
  fromMessageId,
}: {
  app: App;
  apps: App[];
  onAppChange: (id: string) => void;
  customer: string | null;
  fromMessageId: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useProfile(app.credential_profile.id);
  const send = useSendMessage(app.id);
  const source = useMessage(app.id, fromMessageId ?? "", Boolean(fromMessageId));
  const [mode, setMode] = useState<Mode>("compose");
  const [testMode, setTestMode] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => ({
    ...emptyDraft(),
    ...(customer ? { customerIds: [customer] } : {}),
  }));
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showProblems, setShowProblems] = useState(false);
  const [confirming, setConfirming] = useState<SendMessageInput | null>(null);

  // "Duplicate" starts from the content of an earlier message, once, when it has loaded.
  const loaded = source.data;
  const applied = useRef(false);
  useEffect(() => {
    if (!loaded || applied.current) return;
    applied.current = true;
    setDraft((d) => fromMessage(loaded, d));
  }, [loaded]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const patch = <K extends "fcm" | "email" | "sms" | "whatsapp">(k: K, v: Partial<Draft[K]>) =>
    setDraft((d) => ({ ...d, [k]: { ...d[k], ...v } }));
  const setAudience = (audience: AudienceKind) =>
    setDraft((d) => ({
      ...d,
      audience,
      // Broadcasts default to marketing and low priority (P-05) unless the user chose otherwise.
      ...(d.categoryTouched ? {} : { category: audience === "all" ? "marketing" : "transactional" }),
      ...(audience === "all" && d.priority === "normal" ? { priority: "low" as const } : {}),
    }));

  const entries = profile.data?.entries ?? [];
  const aliasesFor = (channel: ComposeChannel) =>
    entries.filter((e) => e.channel === channel && e.is_active && e.credential.status === "active");
  const available = (channel: ComposeChannel) =>
    testMode || profile.isPending || aliasesFor(channel).length > 0;
  const channels = enabledChannels(draft);
  const audience = useAudience(app.id, channels, mode === "compose" && draft.audience === "all");

  const problems = showProblems && mode === "compose" ? validate(draft) : {};
  const err = (key: string) => (problems[key] ? t(problems[key] as never) : null);
  const apiIssues = validationIssues(send.error);

  const request = useMemo<SendMessageInput | null>(() => {
    if (mode === "compose") return buildRequest(draft);
    try {
      return JSON.parse(json) as SendMessageInput;
    } catch {
      return null;
    }
  }, [mode, draft, json]);

  const switchMode = (next: Mode) => {
    if (next === "json") setJson(JSON.stringify(buildRequest(draft), null, 2));
    setJsonError(null);
    setMode(next);
  };

  const review = () => {
    if (mode === "json") {
      if (!request) {
        setJsonError(t("messages.invalidJson"));
        return;
      }
      setJsonError(null);
      setConfirming(request);
      return;
    }
    setShowProblems(true);
    if (Object.keys(validate(draft)).length === 0) setConfirming(buildRequest(draft));
  };

  const submit = () => {
    if (!confirming) return;
    send.mutate(
      { body: confirming, testMode },
      {
        onSuccess: (msg) => navigate(`/apps/${app.id}/messages/${msg.id}`),
        onError: () => setConfirming(null),
      },
    );
  };

  return (
    <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-6">
        <Panel>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <SelectInput
              label={t("send.app")}
              value={app.id}
              onChange={(v) => v && v !== app.id && onAppChange(v)}
              options={apps.map((a) => ({ id: a.id, label: a.name }))}
            />
            <div className="flex flex-col justify-end">
              <SwitchInput
                label={t("send.testMode")}
                description={t("send.testModeHint")}
                isSelected={testMode}
                onChange={setTestMode}
              />
            </div>
          </div>
          <Tabs
            variant="secondary"
            selectedKey={mode}
            onSelectionChange={(k) => switchMode(String(k) as Mode)}
          >
            <Tabs.ListContainer>
              <Tabs.List aria-label={t("send.title")}>
                <Tabs.Tab id="compose">
                  {t("composer.compose")}
                  <Tabs.Indicator />
                </Tabs.Tab>
                <Tabs.Tab id="json">
                  {t("composer.json")}
                  <Tabs.Indicator />
                </Tabs.Tab>
              </Tabs.List>
            </Tabs.ListContainer>
          </Tabs>
        </Panel>

        {mode === "json" ? (
          <Panel title={t("messages.body")} description={t("messages.sendHint")}>
            <TextAreaInput
              name="body"
              label={t("messages.body")}
              value={json}
              onChange={setJson}
              rows={22}
              error={jsonError}
              mono
            />
          </Panel>
        ) : (
          <>
            <Panel title={t("send.audience.title")}>
              <Segmented
                label={t("send.audience.title")}
                value={draft.audience}
                onChange={setAudience}
                options={[
                  { id: "all", label: t("send.audience.all") },
                  { id: "customers", label: t("send.audience.customers") },
                  { id: "addresses", label: t("send.audience.addresses") },
                ]}
              />
              {draft.audience === "all" ? (
                <BroadcastReach
                  channels={channels}
                  data={audience.data}
                  isLoading={audience.isPending && channels.length > 0}
                />
              ) : draft.audience === "customers" ? (
                <CustomerPicker
                  appId={app.id}
                  value={draft.customerIds}
                  onChange={(ids) => set("customerIds", ids)}
                  error={err("customers")}
                />
              ) : (
                <div className="flex flex-col gap-5">
                  <p className="text-sm text-muted">{t("send.audience.addressesHint")}</p>
                  {draft.channels.email ? (
                    <TextAreaInput
                      name="emails"
                      label={t("composer.emails")}
                      placeholder="user@example.com"
                      value={draft.emails}
                      onChange={(v) => set("emails", v)}
                      rows={2}
                      mono
                    />
                  ) : null}
                  {draft.channels.sms || draft.channels.whatsapp ? (
                    <TextAreaInput
                      name="phones"
                      label={t("send.audience.phones")}
                      description={t("send.audience.phonesHint")}
                      placeholder="+6281234567890"
                      value={draft.phones}
                      onChange={(v) => set("phones", v)}
                      rows={2}
                      mono
                    />
                  ) : null}
                  {draft.channels.fcm ? (
                    <TextAreaInput
                      name="tokens"
                      label={t("composer.tokens")}
                      description={t("composer.tokensHint")}
                      value={draft.tokens}
                      onChange={(v) => set("tokens", v)}
                      rows={2}
                      mono
                    />
                  ) : null}
                  {err("addresses") ? <p className="text-sm text-danger">{err("addresses")}</p> : null}
                </div>
              )}
            </Panel>

            <Panel title={t("messages.channels")} description={t("send.channelsHint")}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {COMPOSE_CHANNELS.map((c) => (
                  <div key={c} className="flex flex-col gap-1">
                    <CheckboxInput
                      label={t(`send.channel.${c}`)}
                      isSelected={draft.channels[c] && available(c)}
                      isDisabled={!available(c)}
                      onChange={(v) => set("channels", { ...draft.channels, [c]: v })}
                    />
                    {!available(c) ? (
                      <span className="pl-7 text-xs text-muted">{t("send.noCredential")}</span>
                    ) : null}
                  </div>
                ))}
              </div>
              {err("channels") ? <p className="text-sm text-danger">{err("channels")}</p> : null}
            </Panel>

            {draft.channels.fcm ? (
              <ChannelPanel
                title={t("send.channel.fcm")}
                credential={
                  <CredentialSelect
                    channel="fcm"
                    value={draft.credentials.fcm}
                    aliases={aliasesFor("fcm").map((e) => ({ alias: e.alias, name: e.credential.name }))}
                    onChange={(v) => set("credentials", { ...draft.credentials, fcm: v })}
                    hint={t("send.pushCredentialHint")}
                  />
                }
              >
                <TextInput
                  name="fcm-title"
                  label={t("composer.title")}
                  value={draft.fcm.title}
                  onChange={(v) => patch("fcm", { title: v })}
                  error={err("fcm.title")}
                />
                <TextAreaInput
                  name="fcm-body"
                  label={t("composer.message")}
                  value={draft.fcm.body}
                  onChange={(v) => patch("fcm", { body: v })}
                  rows={3}
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <TextInput
                    name="fcm-image"
                    type="url"
                    label={t("send.fcm.image")}
                    placeholder="https://"
                    value={draft.fcm.image}
                    onChange={(v) => patch("fcm", { image: v })}
                  />
                  <TextInput
                    name="fcm-link"
                    type="url"
                    label={t("send.fcm.link")}
                    description={t("send.fcm.linkHint")}
                    placeholder="https://"
                    value={draft.fcm.link}
                    onChange={(v) => patch("fcm", { link: v })}
                    error={err("fcm.link")}
                  />
                </div>
                <TextAreaInput
                  name="fcm-data"
                  label={t("send.fcm.data")}
                  description={t("send.fcm.dataHint")}
                  placeholder={"order_id=123\nscreen=orders"}
                  value={draft.fcm.data}
                  onChange={(v) => patch("fcm", { data: v })}
                  rows={3}
                  mono
                  error={err("fcm.data")}
                />
              </ChannelPanel>
            ) : null}

            {draft.channels.email ? (
              <ChannelPanel
                title={t("send.channel.email")}
                credential={
                  <CredentialSelect
                    channel="email"
                    value={draft.credentials.email}
                    aliases={aliasesFor("email").map((e) => ({ alias: e.alias, name: e.credential.name }))}
                    onChange={(v) => set("credentials", { ...draft.credentials, email: v })}
                  />
                }
              >
                <TextInput
                  name="email-subject"
                  label={t("send.email.subject")}
                  value={draft.email.subject}
                  onChange={(v) => patch("email", { subject: v })}
                  error={err("email.subject")}
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <TextInput
                    name="email-from"
                    label={t("send.email.fromName")}
                    description={t("send.email.fromNameHint")}
                    value={draft.email.fromName}
                    onChange={(v) => patch("email", { fromName: v })}
                  />
                  <TextInput
                    name="email-reply"
                    type="email"
                    label={t("send.email.replyTo")}
                    value={draft.email.replyTo}
                    onChange={(v) => patch("email", { replyTo: v })}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">{t("send.email.format")}</span>
                  <Segmented
                    label={t("send.email.format")}
                    value={draft.email.format}
                    onChange={(v) => patch("email", { format: v })}
                    options={[
                      { id: "html", label: "HTML" },
                      { id: "text", label: t("send.email.plain") },
                    ]}
                  />
                </div>
                {draft.email.format === "html" ? (
                  <>
                    <TextAreaInput
                      name="email-html"
                      label={t("send.email.html")}
                      description={t("send.email.htmlHint")}
                      placeholder={"<h1>Hello</h1>\n<p>…</p>"}
                      value={draft.email.html}
                      onChange={(v) => patch("email", { html: v })}
                      rows={14}
                      mono
                      error={err("email.html")}
                    />
                    <TextAreaInput
                      name="email-text-alt"
                      label={t("send.email.textAlt")}
                      description={t("send.email.textAltHint")}
                      value={draft.email.text}
                      onChange={(v) => patch("email", { text: v })}
                      rows={4}
                    />
                  </>
                ) : (
                  <TextAreaInput
                    name="email-text"
                    label={t("send.email.text")}
                    value={draft.email.text}
                    onChange={(v) => patch("email", { text: v })}
                    rows={10}
                    error={err("email.text")}
                  />
                )}
              </ChannelPanel>
            ) : null}

            {draft.channels.sms ? (
              <ChannelPanel
                title={t("send.channel.sms")}
                credential={
                  <CredentialSelect
                    channel="sms"
                    value={draft.credentials.sms}
                    aliases={aliasesFor("sms").map((e) => ({ alias: e.alias, name: e.credential.name }))}
                    onChange={(v) => set("credentials", { ...draft.credentials, sms: v })}
                  />
                }
              >
                <TextAreaInput
                  name="sms-text"
                  label={t("send.sms.text")}
                  description={t("preview.sms.count", smsParts(draft.sms.text))}
                  value={draft.sms.text}
                  onChange={(v) => patch("sms", { text: v })}
                  rows={4}
                  error={err("sms.text")}
                />
              </ChannelPanel>
            ) : null}

            {draft.channels.whatsapp ? (
              <ChannelPanel
                title={t("send.channel.whatsapp")}
                credential={
                  <CredentialSelect
                    channel="whatsapp"
                    value={draft.credentials.whatsapp}
                    aliases={aliasesFor("whatsapp").map((e) => ({ alias: e.alias, name: e.credential.name }))}
                    onChange={(v) => set("credentials", { ...draft.credentials, whatsapp: v })}
                  />
                }
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                  <TextInput
                    name="wa-template"
                    label={t("send.whatsapp.template")}
                    description={t("send.whatsapp.templateHint")}
                    value={draft.whatsapp.template}
                    onChange={(v) => patch("whatsapp", { template: v })}
                    error={err("whatsapp.template")}
                    mono
                  />
                  <TextInput
                    name="wa-language"
                    label={t("send.whatsapp.language")}
                    value={draft.whatsapp.language}
                    onChange={(v) => patch("whatsapp", { language: v })}
                    mono
                  />
                </div>
                <TextAreaInput
                  name="wa-variables"
                  label={t("send.whatsapp.variables")}
                  description={t("send.whatsapp.variablesHint")}
                  value={draft.whatsapp.variables}
                  onChange={(v) => patch("whatsapp", { variables: v })}
                  rows={3}
                />
              </ChannelPanel>
            ) : null}

            <Panel title={t("send.delivery")}>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <SelectInput
                  label={t("messages.priority")}
                  value={draft.priority}
                  onChange={(v) => v && set("priority", v as MessagePriority)}
                  options={(["high", "normal", "low"] as const).map((p) => ({
                    id: p,
                    label: t(`priority.${p}`),
                    hint: t(`composer.priorityHint.${p}`),
                  }))}
                />
                <TextInput
                  name="category"
                  label={t("messages.category")}
                  description={t("send.categoryHint")}
                  value={draft.category}
                  onChange={(v) => setDraft((d) => ({ ...d, category: v, categoryTouched: true }))}
                  error={err("category")}
                  mono
                />
              </div>
            </Panel>
          </>
        )}

        {apiIssues.length > 0 ? (
          <Alert status="danger">
            <Alert.Indicator />
            <Alert.Content>
              <ul className="flex flex-col gap-1 text-sm">
                {apiIssues.map((i) => (
                  <li key={`${i.path}-${i.message}`}>
                    {i.path ? <span className="font-mono text-[12.5px]">{i.path}: </span> : null}
                    {i.message}
                  </li>
                ))}
              </ul>
            </Alert.Content>
          </Alert>
        ) : send.error ? (
          <ErrorBlock error={send.error} />
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-3">
          {Object.keys(problems).length ? (
            <span className="text-sm text-danger">{t("send.errors.fix")}</span>
          ) : null}
          <Button onPress={review} isDisabled={send.isPending}>
            <PaperPlane />
            {t("send.review")}
          </Button>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-6 xl:sticky xl:top-24">
        <Panel title={t("send.preview")} description={t("send.previewHint")}>
          <Preview items={request?.channels ?? []} appName={app.name} to={previewAddress(draft, mode)} />
        </Panel>
      </div>

      <ConfirmSend
        request={confirming}
        app={app}
        testMode={testMode}
        reach={draft.audience === "all" && mode === "compose" ? audience.data?.reachable : undefined}
        isPending={send.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={submit}
      />
    </div>
  );
}

/** The email preview shows a real address when the form has exactly one. */
function previewAddress(d: Draft, mode: Mode): string | null {
  if (mode !== "compose") return null;
  if (d.audience === "addresses") {
    const emails = splitList(d.emails);
    return emails.length === 1 ? emails[0]! : null;
  }
  return null;
}

function ChannelPanel({
  title,
  credential,
  children,
}: {
  title: string;
  credential: ReactNode;
  children: ReactNode;
}) {
  return (
    <Panel title={title}>
      {children}
      {credential}
    </Panel>
  );
}

const DEFAULT = "__default__";

function CredentialSelect({
  channel,
  value,
  aliases,
  onChange,
  hint,
}: {
  channel: ComposeChannel;
  value: string | undefined;
  aliases: Array<{ alias: string; name: string }>;
  onChange: (alias: string | undefined) => void;
  hint?: string;
}) {
  const { t } = useTranslation();
  if (aliases.length < 2 && !value) return null;
  return (
    <SelectInput
      label={t("send.credential")}
      description={hint ?? t("send.credentialHint")}
      value={value ?? DEFAULT}
      onChange={(v) => onChange(!v || v === DEFAULT ? undefined : v)}
      options={[
        { id: DEFAULT, label: t("send.credentialDefault"), hint: t(`channels.${channel}`) },
        ...aliases.map((a) => ({ id: a.alias, label: a.alias, hint: a.name })),
      ]}
    />
  );
}

function BroadcastReach({
  channels,
  data,
  isLoading,
}: {
  channels: ComposeChannel[];
  data:
    | {
        customers: number;
        reachable: number;
        per_channel: Array<{ channel: string; customers: number; addresses: number }>;
      }
    | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (channels.length === 0) return <p className="text-sm text-muted">{t("send.reach.pickChannel")}</p>;
  if (isLoading || !data) return <p className="text-sm text-muted">{t("common.loading")}</p>;
  return (
    <div className="flex flex-col gap-3">
      <Callout tone={data.reachable ? "info" : "warning"}>
        {data.reachable
          ? t("send.reach.summary", {
              reachable: fmt.number(data.reachable),
              total: fmt.number(data.customers),
            })
          : t("send.reach.nobody")}
      </Callout>
      <ul className="flex flex-col gap-2">
        {data.per_channel.map((c) => (
          <li key={c.channel} className="flex flex-wrap items-center gap-2 text-sm">
            <ChannelChip channel={c.channel as ComposeChannel} />
            <span className="text-muted">
              {c.channel === "fcm"
                ? t("send.reach.devices", {
                    customers: fmt.number(c.customers),
                    devices: fmt.number(c.addresses),
                  })
                : t("send.reach.customers", { customers: fmt.number(c.customers) })}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted">{t("send.reach.hint")}</p>
    </div>
  );
}

function Preview({
  items,
  appName,
  to,
}: {
  items: SendMessageInput["channels"];
  appName: string;
  to: string | null;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState<string | null>(null);
  if (!items.length) {
    return <EmptyBlock icon={Envelope} title={t("send.previewEmpty")} />;
  }
  const current = items.find((i) => i.type === active) ?? items[0]!;
  return (
    <div className="flex flex-col gap-4">
      {items.length > 1 ? (
        <Segmented
          label={t("preview.channel")}
          value={current.type}
          onChange={setActive}
          options={items.map((i) => ({ id: i.type, label: t(`channels.${i.type}`) }))}
        />
      ) : null}
      <ChannelPreview
        channel={current.type}
        payload={(current.payload ?? {}) as Record<string, unknown>}
        appName={appName}
        to={current.type === "email" ? to : null}
      />
    </div>
  );
}

function ConfirmSend({
  request,
  app,
  testMode,
  reach,
  isPending,
  onCancel,
  onConfirm,
}: {
  request: SendMessageInput | null;
  app: App;
  testMode: boolean;
  reach: number | undefined;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const to = request?.to;
  const a = to?.addresses;
  const addressCount = (a?.emails?.length ?? 0) + (a?.phones?.length ?? 0) + (a?.push_tokens?.length ?? 0);
  const recipients = to?.all_customers
    ? reach === undefined
      ? t("send.confirm.allUnknown")
      : t("send.confirm.all", { count: reach, n: fmt.number(reach) })
    : [
        to?.customer_ids?.length ? t("send.confirm.customers", { count: to.customer_ids.length }) : null,
        addressCount ? t("send.confirm.addresses", { count: addressCount }) : null,
      ]
        .filter(Boolean)
        .join(" · ");
  const broadcastLive = Boolean(to?.all_customers) && !testMode;

  return (
    <FormDialog
      isOpen={request !== null}
      onOpenChange={(open) => !open && !isPending && onCancel()}
      title={t("send.confirm.title")}
      description={testMode ? t("send.confirm.test") : t("send.confirm.live")}
      submitLabel={testMode ? t("composer.sendTest") : t("send.confirm.send")}
      submitVariant={broadcastLive ? "danger" : "primary"}
      isPending={isPending}
      onSubmit={onConfirm}
    >
      <KeyValue
        items={[
          { label: t("send.app"), value: app.name },
          { label: t("send.confirm.recipients"), value: recipients || "—" },
          {
            label: t("messages.channels"),
            value: (
              <div className="flex flex-wrap gap-1">
                {(request?.channels ?? []).map((c) => (
                  <ChannelChip key={c.type} channel={c.type} />
                ))}
              </div>
            ),
          },
          { label: t("messages.priority"), value: t(`priority.${request?.priority ?? "normal"}`) },
          { label: t("messages.category"), value: request?.category ?? "transactional" },
          { label: t("messages.mode"), value: testMode ? t("keys.test") : t("keys.live") },
        ]}
      />
      {broadcastLive ? <Callout tone="warning">{t("send.confirm.broadcastWarning")}</Callout> : null}
    </FormDialog>
  );
}
